// src/hooks/useFilters.js
import { useState, useMemo } from "react";
import { getAllEmails } from "../../../utils/emailHelpers";

export const useFilters = (individuals, candidateMap = {}, bizdevProcessMap = {}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [showOnlyWithPhone, setShowOnlyWithPhone] = useState(false);
  const [showOnlyWithProfessionalEmail, setShowOnlyWithProfessionalEmail] =
    useState(false);
  const [showOnlyWithPersonalEmail, setShowOnlyWithPersonalEmail] =
    useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortBy, setSortBy] = useState("updatedAt"); // "updatedAt" ou "createdAt"

  // Nouveaux filtres
  const [cityFilter, setCityFilter] = useState("");
  const [personalEmailFilter, setPersonalEmailFilter] = useState("");
  const [professionalEmailFilter, setProfessionalEmailFilter] = useState("");
  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [yearsOfExperienceFilter, setYearsOfExperienceFilter] = useState(0);
  const [salaryFilters, setSalaryFilters] = useState({
    currentMin: "",
    currentMax: "",
    expectedMin: "",
    expectedMax: "",
  });
  const [openToJobOpportunities, setOpenToJobOpportunities] = useState(false);
  const [selectedBizdevProcesses, setSelectedBizdevProcesses] = useState([]);

  // Fonction pour calculer les années d'expérience totales en déduisant les chevauchements
  const calculateYearsOfExperience = (experiences) => {
    if (!experiences || experiences.length === 0) return 0;

    // Créer un tableau de périodes avec startDate et endDate
    const periods = experiences
      .filter((exp) => exp.startDate)
      .map((exp) => ({
        start: new Date(exp.startDate),
        end: exp.endDate ? new Date(exp.endDate) : new Date(), // Si pas de date de fin, c'est le poste actuel
      }))
      .sort((a, b) => a.start - b.start); // Trier par date de début

    if (periods.length === 0) return 0;

    // Fusionner les périodes qui se chevauchent
    const mergedPeriods = [];
    let currentPeriod = { ...periods[0] };

    for (let i = 1; i < periods.length; i++) {
      const nextPeriod = periods[i];

      // Si la période suivante chevauche ou est adjacente à la période actuelle
      if (nextPeriod.start <= currentPeriod.end) {
        // Fusionner : étendre la fin de la période actuelle si nécessaire
        currentPeriod.end = new Date(
          Math.max(currentPeriod.end.getTime(), nextPeriod.end.getTime())
        );
      } else {
        // Pas de chevauchement : sauvegarder la période actuelle et commencer une nouvelle
        mergedPeriods.push(currentPeriod);
        currentPeriod = { ...nextPeriod };
      }
    }
    // Ajouter la dernière période
    mergedPeriods.push(currentPeriod);

    // Calculer la durée totale en années
    const totalMilliseconds = mergedPeriods.reduce((total, period) => {
      return total + (period.end - period.start);
    }, 0);

    // Convertir en années (approximativement)
    const yearsOfExperience = totalMilliseconds / (1000 * 60 * 60 * 24 * 365.25);

    return Math.round(yearsOfExperience * 10) / 10; // Arrondir à 1 décimale
  };

  // Apply all filters
  const filteredIndividuals = useMemo(() => {
    let result = [...individuals];

    // Filter by search term (name, company, or email)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((individual) => {
        const nameMatch =
          individual.Name &&
          individual.Name.toLowerCase().includes(searchLower);
        const companyMatch =
          individual.company &&
          individual.company.toLowerCase().includes(searchLower);
        const personalEmailMatch = getAllEmails(individual.personalEmail).some(email =>
          email.toLowerCase().includes(searchLower)
        );
        const professionalEmailMatch = getAllEmails(individual.professionalEmail).some(email =>
          email.toLowerCase().includes(searchLower)
        );
        return (
          nameMatch ||
          companyMatch ||
          personalEmailMatch ||
          professionalEmailMatch
        );
      });
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      result = result.filter((individual) =>
        selectedTags.every(
          (tag) =>
            individual.tags &&
            individual.tags.some(
              (individualTag) =>
                individualTag.name.toLowerCase() === tag.toLowerCase()
            )
        )
      );
    }

    // Filter by positions
    if (selectedPositions.length > 0) {
      result = result.filter((individual) => {
        const individualId = individual._id;

        // Check if individual is in any of the selected positions
        return (
          candidateMap[individualId] &&
          candidateMap[individualId].some((position) =>
            selectedPositions.includes(position.positionId)
          )
        );
      });
    }

    // Filter by phone number
    if (showOnlyWithPhone) {
      result = result.filter((individual) => {
        if (!individual.phoneNumber) return false;

        const phoneString = individual.phoneNumber.trim().toLowerCase();
        const isPlaceholder =
          phoneString === "-" ||
          phoneString === "téléphone indisponible" ||
          phoneString === "null" ||
          phoneString === "telephone indisponible";

        return !isPlaceholder;
      });
    }

    // Filter by professional email
    if (showOnlyWithProfessionalEmail) {
      result = result.filter(
        (individual) => getAllEmails(individual.professionalEmail).length > 0
      );
    }

    // Filter by personal email
    if (showOnlyWithPersonalEmail) {
      result = result.filter(
        (individual) => getAllEmails(individual.personalEmail).length > 0
      );
    }

    // Filter by city
    if (cityFilter.trim()) {
      const cityLower = cityFilter.toLowerCase();
      result = result.filter(
        (individual) =>
          individual.city && individual.city.toLowerCase().includes(cityLower)
      );
    }

    // Filter by personal email
    if (personalEmailFilter.trim()) {
      const emailLower = personalEmailFilter.toLowerCase();
      result = result.filter(
        (individual) => getAllEmails(individual.personalEmail).some(email =>
          email.toLowerCase().includes(emailLower)
        )
      );
    }

    // Filter by professional email
    if (professionalEmailFilter.trim()) {
      const emailLower = professionalEmailFilter.toLowerCase();
      result = result.filter(
        (individual) => getAllEmails(individual.professionalEmail).some(email =>
          email.toLowerCase().includes(emailLower)
        )
      );
    }

    // Filter by job title
    if (jobTitleFilter.trim()) {
      const jobTitleLower = jobTitleFilter.toLowerCase();
      result = result.filter(
        (individual) =>
          individual.jobTitle &&
          individual.jobTitle.toLowerCase().includes(jobTitleLower)
      );
    }

    // Filter by years of experience
    if (yearsOfExperienceFilter > 0) {
      result = result.filter((individual) => {
        const years = calculateYearsOfExperience(individual.experiences);
        return years >= yearsOfExperienceFilter;
      });
    }

    // Filter by keyword (recherche dans description, jobTitle, tags, formation, experiences, education, text)
    // Supporte plusieurs mots-clés séparés par des espaces (recherche ET)
    if (keywordFilter.trim()) {
      // Séparer les mots-clés par des espaces et filtrer les mots vides
      const keywords = keywordFilter
        .toLowerCase()
        .split(/\s+/)
        .filter(keyword => keyword.length > 0);

      result = result.filter((individual) => {
        // Construire le texte complet à analyser pour cet individu
        const fullText = [
          individual.description || "",
          individual.jobTitle || "",
          individual.formation || "",
          // Tags
          ...(individual.tags || []).map(tag => tag.name),
          // Experiences
          ...(individual.experiences || []).map(exp =>
            `${exp.name || ""} ${exp.description || ""} ${exp.company || ""}`
          ),
          // Education
          ...(individual.education || []).map(edu =>
            `${edu.name || ""} ${edu.institution || ""} ${edu.fieldOfStudy || ""}`
          ),
          // Text/Notes
          ...(individual.text || []).map(txt => txt.content || "")
        ].join(" ").toLowerCase();

        // Vérifier que TOUS les mots-clés sont présents (recherche ET)
        return keywords.every(keyword => fullText.includes(keyword));
      });
    }

    // Filter by salary ranges
    if (salaryFilters.currentMin || salaryFilters.currentMax) {
      result = result.filter((individual) => {
        const salary = individual.SA;
        if (!salary) return false;

        const min = salaryFilters.currentMin
          ? parseFloat(salaryFilters.currentMin)
          : 0;
        const max = salaryFilters.currentMax
          ? parseFloat(salaryFilters.currentMax)
          : Infinity;

        return salary >= min && salary <= max;
      });
    }

    if (salaryFilters.expectedMin || salaryFilters.expectedMax) {
      result = result.filter((individual) => {
        const salary = individual.SE;
        if (!salary) return false;

        const min = salaryFilters.expectedMin
          ? parseFloat(salaryFilters.expectedMin)
          : 0;
        const max = salaryFilters.expectedMax
          ? parseFloat(salaryFilters.expectedMax)
          : Infinity;

        return salary >= min && salary <= max;
      });
    }

    // Filter by openToJobOpportunities
    if (openToJobOpportunities) {
      result = result.filter((individual) => individual.openToJobOpportunities === true);
    }

    // Filter by BizDev Process
    if (selectedBizdevProcesses.length > 0) {
      result = result.filter((individual) => {
        const individualId = individual._id;

        // Check if individual is in any of the selected bizdev processes
        return (
          bizdevProcessMap[individualId] &&
          bizdevProcessMap[individualId].some((process) =>
            selectedBizdevProcesses.includes(process.processId)
          )
        );
      });
    }

    // Apply sort
    result.sort((a, b) => {
      const fieldToSort = sortBy === "updatedAt" ? "updatedAt" : "createdAt";
      const dateA = new Date(a[fieldToSort]).getTime();
      const dateB = new Date(b[fieldToSort]).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    return result;
  }, [
    individuals,
    searchTerm,
    selectedTags,
    selectedPositions,
    showOnlyWithPhone,
    showOnlyWithProfessionalEmail,
    showOnlyWithPersonalEmail,
    sortOrder,
    sortBy,
    candidateMap,
    cityFilter,
    personalEmailFilter,
    professionalEmailFilter,
    jobTitleFilter,
    keywordFilter,
    yearsOfExperienceFilter,
    salaryFilters,
    openToJobOpportunities,
    selectedBizdevProcesses,
    bizdevProcessMap,
  ]);

  return {
    searchTerm,
    setSearchTerm,
    selectedTags,
    setSelectedTags,
    selectedPositions,
    setSelectedPositions,
    showOnlyWithPhone,
    setShowOnlyWithPhone,
    showOnlyWithProfessionalEmail,
    setShowOnlyWithProfessionalEmail,
    showOnlyWithPersonalEmail,
    setShowOnlyWithPersonalEmail,
    sortOrder,
    setSortOrder,
    sortBy,
    setSortBy,
    filteredIndividuals,
    // Nouveaux filtres
    cityFilter,
    setCityFilter,
    personalEmailFilter,
    setPersonalEmailFilter,
    professionalEmailFilter,
    setProfessionalEmailFilter,
    jobTitleFilter,
    setJobTitleFilter,
    keywordFilter,
    setKeywordFilter,
    yearsOfExperienceFilter,
    setYearsOfExperienceFilter,
    salaryFilters,
    setSalaryFilters,
    openToJobOpportunities,
    setOpenToJobOpportunities,
    selectedBizdevProcesses,
    setSelectedBizdevProcesses,
  };
};
